import { html, type TemplateResult } from "lit-html";
import type { AppState } from "../data/store";
import type { BuyerRow, ConversationStatus } from "../data/types";
import { ALL_STATUSES } from "../data/types";
import { store } from "../data/store";
import { renderAccountTabs } from "./AccountTabs";
import { renderBulkActionBar } from "./BulkActionBar";
import { formatRelative } from "../utils/time";
import { activeBuyers, applyAccountFilter, applyPipelineFilters, dollars, filtersActive, initials } from "../utils/format";
import { iconSearch, iconTrash } from "./icons";

/**
 * Pipeline cube — every non-paid buyer.
 * Account tabs in the head; click any row to open the EditorSheet.
 */
export function renderPipelineCube(state: AppState): TemplateResult {
  const allInPipeline = applyAccountFilter(activeBuyers(state.buyers), state.selectedAccountId)
    .filter((b) => b.conv_status !== "paid");
  const rows = applyPipelineFilters(allInPipeline, state.filters);

  return html`
    <div class="cube">
      <div class="cube__head cube-shell__drag">
        <span class="cube__title">Buyers</span>
        <span class="cube__count">
          ${rows.length}${filtersActive(state.filters) ? html`<span class="cube__count-total"> / ${allInPipeline.length}</span>` : ""}
        </span>
        <div class="cube__head-tabs">
          ${renderAccountTabs(state.accounts, state.selectedAccountId)}
        </div>
      </div>
      ${renderFilterBar(state)}
      <div class="cube__body">
        ${renderTable(rows, state)}
      </div>
      ${renderBulkActionBar(state, "desktop")}
    </div>
  `;
}

function renderFilterBar(state: AppState): TemplateResult {
  const f = state.filters;
  const nonPaidStatuses: ConversationStatus[] = ALL_STATUSES.filter((s) => s !== "paid");
  return html`
    <div class="filter-bar">
      <label class="filter-bar__search">
        <span class="filter-bar__search-icon">${iconSearch()}</span>
        <input class="filter-bar__input" type="search"
               placeholder="Search buyer or listing…"
               .value=${f.search}
               @input=${(e: Event) => store.setSearch((e.target as HTMLInputElement).value)} />
      </label>
      <div class="filter-bar__statuses">
        ${nonPaidStatuses.map((s) => {
          const active = f.statuses.includes(s);
          return html`
            <button type="button"
                    class="filter-chip pill pill--${s} ${active ? "filter-chip--active" : ""}"
                    @click=${() => store.toggleStatusFilter(s)}>
              ${s}
            </button>
          `;
        })}
      </div>
      <label class="filter-bar__unread">
        <input type="checkbox" .checked=${f.unreadOnly}
               @change=${(e: Event) => store.setUnreadOnly((e.target as HTMLInputElement).checked)} />
        <span>Unread only</span>
      </label>
      ${filtersActive(f) ? html`
        <button class="filter-bar__reset" type="button"
                @click=${() => store.resetFilters()}>Clear</button>
      ` : ""}
    </div>
  `;
}

function renderTable(rows: BuyerRow[], state: AppState): TemplateResult {
  if (state.loading && state.buyers.length === 0) {
    return html`
      <div class="skeleton"></div><div class="skeleton"></div>
      <div class="skeleton"></div><div class="skeleton"></div>
    `;
  }
  if (state.error && state.buyers.length === 0) {
    return html`<div class="empty">Couldn't reach the API: ${state.error}</div>`;
  }
  if (rows.length === 0) {
    return html`<div class="empty">No active buyers in this view.</div>`;
  }

  const visibleConvIds = rows.map((r) => r.conv_id).filter((x): x is string => x !== null);
  const allChecked = visibleConvIds.length > 0
    && visibleConvIds.every((id) => state.selectedConvIds.has(id));

  return html`
    <div class="buyers-row buyers-row--head">
      <span class="buyers-row__check">
        <input type="checkbox" .checked=${allChecked}
               @change=${() => store.selectAllConvs(visibleConvIds)}
               title="Select all visible" />
      </span>
      <span>Buyer</span>
      <span>Account</span>
      <span>Listing</span>
      <span>Status</span>
      <span>Qty</span>
      <span>Price</span>
      <span>Last activity</span>
    </div>
    ${rows.map((r) => renderRow(r, state))}
  `;
}

function renderRow(b: BuyerRow, state: AppState): TemplateResult {
  const clickable = b.conv_id != null;
  const unread    = b.conv_unread_count > 0;
  const selected  = b.conv_id != null && state.selectedConvIds.has(b.conv_id);
  return html`
    <div class="buyers-row ${clickable ? "buyers-row--clickable" : ""} ${unread ? "buyers-row--unread" : ""} ${selected ? "buyers-row--selected" : ""}"
         @click=${(e: Event) => {
           // Don't open the editor when the click came from the checkbox.
           if ((e.target as HTMLElement).closest(".buyers-row__check")) return;
           if (b.conv_id) store.openEditor(b.conv_id);
         }}>
      <span class="buyers-row__check">
        ${b.conv_id ? html`
          <input type="checkbox" .checked=${selected}
                 @change=${() => store.toggleConvSelected(b.conv_id!)}
                 @click=${(e: Event) => e.stopPropagation()} />
        ` : ""}
      </span>
      <span class="buyer-name">
        <span class="buyer-avatar">${initials(b.display_name)}</span>
        <span>${b.display_name}</span>
        ${b.duplicate_of ? html`
          <span class="dupe-badge dupe-badge--${b.duplicate_match}"
                title="Duplicate of ${b.duplicate_primary_name ?? "primary buyer"}${b.duplicate_match === "name" ? " — matched by name (verify)" : " — matched by phone"}">DUPE</span>
        ` : ""}
        ${unread ? html`<span class="unread-badge" title="${b.conv_unread_count} unread message${b.conv_unread_count === 1 ? "" : "s"}">${b.conv_unread_count}</span>` : ""}
      </span>
      <span class="buyer-meta">${b.account_username ?? html`<span class="muted">—</span>`}</span>
      <span class="listing-cell">
        <span class="listing-title">${b.listing_title ?? html`<span class="muted">—</span>`}</span>
        ${b.listing_price_cents != null
          ? html`<span class="listing-price">list ${dollars(b.listing_price_cents)}</span>`
          : ""}
      </span>
      <span>${renderStatus(b.conv_status)}</span>
      <span class="qty-cell">${b.conv_tickets_count ?? html`<span class="muted">—</span>`}</span>
      <span>${renderPrice(b)}</span>
      <span class="time-cell">
        ${b.conv_last_activity_at ? formatRelative(b.conv_last_activity_at) : html`<span class="muted">—</span>`}
        ${b.conv_id ? html`
          <button class="buyers-row__archive"
                  type="button"
                  aria-label="Archive ${b.display_name}"
                  title="Archive ${b.display_name}"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    if (confirm(`Archive ${b.display_name}? You can restore them from the Archived cube.`)) {
                      void store.archiveConversation(b.conv_id!);
                    }
                  }}>${iconTrash()}</button>
        ` : ""}
      </span>
    </div>
  `;
}

function renderStatus(s: ConversationStatus | null): TemplateResult {
  if (!s) return html`<span class="muted">—</span>`;
  return html`<span class="pill pill--${s}">${s}</span>`;
}

function renderPrice(b: BuyerRow): TemplateResult {
  if (b.conv_agreed_price_cents != null) {
    const per = b.conv_tickets_count && b.conv_tickets_count > 0
      ? b.conv_agreed_price_cents / b.conv_tickets_count
      : null;
    return html`
      <span class="price-cell price-cell--agreed">${dollars(b.conv_agreed_price_cents)}</span>
      ${per != null ? html`<span class="price-sub">${dollars(per)}/ea</span>` : ""}
    `;
  }
  if (b.listing_price_cents != null) {
    return html`<span class="price-cell muted">${dollars(b.listing_price_cents)}</span>`;
  }
  return html`<span class="muted">—</span>`;
}
